import { useMemo, useState } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UIExercise, UIGrouping } from "@/integrations/backend/workouts";
import type { Difficulty } from "@/integrations/backend/enums";
import { Button } from "@/components/ui/button";
import { ArrowDown, ArrowUp, Dumbbell, GripVertical, Link2, X } from "lucide-react";

type DragKind =
  | { type: "exercise"; exerciseId: string; sourceGroupingId: string }
  | { type: "group"; groupingId: string };

type NumericField = "sets" | "reps" | "time_seconds" | "rest_seconds" | "weight_kg" | "weight_lbs";

function numericOnly(next: string): string {
  // Allow empty while typing; otherwise only digits.
  if (next === "") return "";
  return next.replace(/[^\d]/g, "");
}

function clampNonNegativeInt(n: number): number {
  if (!Number.isFinite(n) || Number.isNaN(n)) return 0;
  return Math.max(0, Math.trunc(n));
}

export default function DBWorkoutCanvas({
  templateId,
  groupings,
  exerciseNameById,
  activeDifficulties,
  onUpdateGroupingSets,
  onUpdateGroupingRest,
  onUpdateExercise,
  onReorderGroupings,
  onReorderExercises,
  onExtractExerciseToIndex,
  onCombineExercises,
  onDeleteExercise,
  onMoveExerciseToGroupingEnd,
}: {
  templateId: string;
  groupings: UIGrouping[];
  exerciseNameById: Record<string,string>;
  activeDifficulties: Set<Difficulty>;
  onUpdateGroupingSets: (groupingId: string, sets: number) => void;
  onUpdateGroupingRest: (groupingId: string, restSeconds: number) => void;
  onUpdateExercise: (exerciseId: string, patch: Partial<UIExercise>) => void;
  onReorderGroupings: (groupingIdsInOrder: string[]) => Promise<void> | void;
  onReorderExercises: (groupingId: string, exerciseIdsInOrder: string[]) => Promise<void> | void;
  onExtractExerciseToIndex: (exerciseId: string, canvasIndex: number) => Promise<void> | void;
  onCombineExercises: (droppedExerciseId: string, targetExerciseId: string) => Promise<void> | void;
  onDeleteExercise: (exerciseId: string) => Promise<void> | void;
  onMoveExerciseToGroupingEnd: (targetGroupingId: string, exerciseId: string) => Promise<void> | void;
}) {
  const [drag, setDrag] = useState<DragKind | null>(null);
  const [canvasInsertIndex, setCanvasInsertIndex] = useState<number | null>(null);
  const [combineTarget, setCombineTarget] = useState<string | null>(null);
  const [draftNumberByKey, setDraftNumberByKey] = useState<Record<string, string>>({});

  const groupingIds = useMemo(() => groupings.map(g => g.id), [groupings]);

  const visibleGroupings = useMemo(() => {
    return groupings
      .map((g) => {
        const visibleExercises = g.exercises.filter((ex) => {
          const difficulty = ex.difficulty || "Beginner";
          return activeDifficulties.has(difficulty);
        });
        return { grouping: g, visibleExercises };
      })
      .filter((x) => x.visibleExercises.length > 0);
  }, [groupings, activeDifficulties]);

  const visibleGroupingIds = useMemo(() => visibleGroupings.map((x) => x.grouping.id), [visibleGroupings]);

  const getDraftKey = (exerciseId: string, field: NumericField) => `${exerciseId}:${field}`;
  const getDraftValue = (exerciseId: string, field: NumericField, current: number) => {
    const key = getDraftKey(exerciseId, field);
    return draftNumberByKey[key] ?? String(current ?? 0);
  };
  const setDraftValue = (exerciseId: string, field: NumericField, next: string) => {
    const key = getDraftKey(exerciseId, field);
    setDraftNumberByKey((prev) => ({ ...prev, [key]: numericOnly(next) }));
  };
  const commitDraft = (exerciseId: string, field: NumericField, fallback: number) => {
    const key = getDraftKey(exerciseId, field);
    const raw = draftNumberByKey[key];
    if (raw === undefined) return; // nothing to commit

    const nextNum = raw === "" ? 0 : clampNonNegativeInt(parseInt(raw, 10));
    const prevNum = clampNonNegativeInt(fallback);
    setDraftNumberByKey((prev) => {
      const { [key]: _, ...rest } = prev;
      return rest;
    });
    if (nextNum !== prevNum) {
      onUpdateExercise(exerciseId, { [field]: nextNum } as any);
    }
  };

  const handleGroupDragStart = (groupingId: string) => (e: React.DragEvent) => {
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", groupingId); } catch {}
    setDrag({ type: "group", groupingId });
  };

  const handleExerciseDragStart = (exerciseId: string, sourceGroupingId: string) => (e: React.DragEvent) => {
    e.stopPropagation();
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", exerciseId); } catch {}
    setDrag({ type: "exercise", exerciseId, sourceGroupingId });
  };

  const clearDnD = () => {
    setDrag(null);
    setCanvasInsertIndex(null);
    setCombineTarget(null);
  };

  const onCanvasDragOverGap = (idx: number) => (e: React.DragEvent) => {
    e.preventDefault();
    if (!drag) return;
    setCanvasInsertIndex(idx);
  };

  const onCanvasDropAt = (idx: number) => (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!drag) return;
    if (drag.type === "group") {
      // idx is a visible index (0..visibleGroupingIds.length). We translate it to a stable reorder in full order.
      if (!visibleGroupingIds.includes(drag.groupingId)) return clearDnD();
      const full = groupingIds.slice();
      const visible = visibleGroupingIds.slice();

      const fromVisible = visible.indexOf(drag.groupingId);
      if (fromVisible === -1) return clearDnD();
      const nextVisible = visible.slice();
      nextVisible.splice(fromVisible, 1);
      const clampedIdx = Math.min(Math.max(idx, 0), nextVisible.length);
      nextVisible.splice(clampedIdx, 0, drag.groupingId);

      const withoutDragged = full.filter((id) => id !== drag.groupingId);
      const newPos = nextVisible.indexOf(drag.groupingId);
      const prev = newPos > 0 ? nextVisible[newPos - 1] : null;
      const next = newPos < nextVisible.length - 1 ? nextVisible[newPos + 1] : null;

      let insertIndex = 0;
      if (prev) {
        insertIndex = withoutDragged.indexOf(prev) + 1;
      } else if (next) {
        insertIndex = withoutDragged.indexOf(next);
      } else {
        insertIndex = 0;
      }
      if (insertIndex < 0) insertIndex = 0;
      withoutDragged.splice(insertIndex, 0, drag.groupingId);
      onReorderGroupings(withoutDragged);
    } else if (drag.type === "exercise") {
      // extracting single exercise to new grouping at canvas index
      // idx is visible index; translate to a full insertion index based on current full grouping order.
      const visibleBefore = Math.min(Math.max(idx, 0), visibleGroupingIds.length);
      let fullInsertIndex = groupingIds.length;
      if (visibleGroupingIds.length > 0) {
        const nextVisibleId = visibleGroupingIds[visibleBefore] ?? null;
        if (nextVisibleId) {
          const fullIdx = groupingIds.indexOf(nextVisibleId);
          fullInsertIndex = fullIdx === -1 ? groupingIds.length : fullIdx;
        } else {
          // after last visible: insert after the last visible group (before any trailing hidden groups)
          const lastVisibleId = visibleGroupingIds[visibleGroupingIds.length - 1];
          const fullIdx = groupingIds.indexOf(lastVisibleId);
          fullInsertIndex = fullIdx === -1 ? groupingIds.length : fullIdx + 1;
        }
      }
      onExtractExerciseToIndex(drag.exerciseId, fullInsertIndex);
    }
    clearDnD();
  };

  const onExerciseDragEnter = (exerciseId: string) => (e: React.DragEvent) => {
    e.preventDefault();
    if (!drag || drag.type !== "exercise") return;
    setCombineTarget(exerciseId);
  };

  const onExerciseDropOn = (exerciseId: string) => (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!drag) return clearDnD();
    if (drag.type === "exercise") {
      onCombineExercises(drag.exerciseId, exerciseId);
    }
    clearDnD();
  };

  const buildFullExerciseOrderFromVisible = (args: {
    groupingId: string;
    draggedExerciseId: string;
    visibleExerciseIds: string[];
    dropVisibleIndex: number;
  }) => {
    const grouping = groupings.find((g) => g.id === args.groupingId);
    if (!grouping) return null;

    const full = grouping.exercises.map((e) => e.id);
    const visible = args.visibleExerciseIds.slice();
    const fromVisible = visible.indexOf(args.draggedExerciseId);

    // When the dragged exercise is already in this group's visible list, adjust insertion index accordingly.
    let insertAt = Math.min(Math.max(args.dropVisibleIndex, 0), visible.length);
    if (fromVisible !== -1) {
      if (fromVisible < insertAt) insertAt -= 1;
      visible.splice(fromVisible, 1);
    }
    visible.splice(insertAt, 0, args.draggedExerciseId);

    const withoutDragged = full.filter((id) => id !== args.draggedExerciseId);
    const newPos = visible.indexOf(args.draggedExerciseId);
    const prev = newPos > 0 ? visible[newPos - 1] : null;
    const next = newPos < visible.length - 1 ? visible[newPos + 1] : null;

    let insertIndex = 0;
    if (prev) {
      insertIndex = withoutDragged.indexOf(prev) + 1;
    } else if (next) {
      insertIndex = withoutDragged.indexOf(next);
    } else {
      insertIndex = 0;
    }
    if (insertIndex < 0) insertIndex = 0;
    withoutDragged.splice(insertIndex, 0, args.draggedExerciseId);
    return withoutDragged;
  };

  const moveExerciseUp = (groupingId: string, exerciseId: string) => {
    const groupIdx = groupings.findIndex((g) => g.id === groupingId);
    if (groupIdx === -1) return;
    const g = groupings[groupIdx];
    const exIdx = g.exercises.findIndex((e) => e.id === exerciseId);
    if (exIdx === -1) return;

    if (exIdx > 0) {
      const ids = g.exercises.map((e) => e.id);
      const tmp = ids[exIdx - 1];
      ids[exIdx - 1] = ids[exIdx];
      ids[exIdx] = tmp;
      onReorderExercises(groupingId, ids);
      return;
    }

    if (groupIdx > 0) {
      const ids = groupings.map((g) => g.id);
      const tmp = ids[groupIdx - 1];
      ids[groupIdx - 1] = ids[groupIdx];
      ids[groupIdx] = tmp;
      onReorderGroupings(ids);
    }
  };

  const moveExerciseDown = (groupingId: string, exerciseId: string) => {
    const groupIdx = groupings.findIndex((g) => g.id === groupingId);
    if (groupIdx === -1) return;
    const g = groupings[groupIdx];
    const exIdx = g.exercises.findIndex((e) => e.id === exerciseId);
    if (exIdx === -1) return;

    if (exIdx < g.exercises.length - 1) {
      const ids = g.exercises.map((e) => e.id);
      const tmp = ids[exIdx + 1];
      ids[exIdx + 1] = ids[exIdx];
      ids[exIdx] = tmp;
      onReorderExercises(groupingId, ids);
      return;
    }

    if (groupIdx < groupings.length - 1) {
      const ids = groupings.map((g) => g.id);
      const tmp = ids[groupIdx + 1];
      ids[groupIdx + 1] = ids[groupIdx];
      ids[groupIdx] = tmp;
      onReorderGroupings(ids);
    }
  };

  const joinWithAbove = (groupingId: string, exerciseId: string) => {
    const groupIdx = groupings.findIndex((g) => g.id === groupingId);
    if (groupIdx <= 0) return;
    const prevGroup = groupings[groupIdx - 1];
    if (!prevGroup || prevGroup.exercises.length === 0) return;
    const targetExerciseId = prevGroup.exercises[prevGroup.exercises.length - 1].id;
    onCombineExercises(exerciseId, targetExerciseId);
  };

  const ungroupToBelow = (groupingId: string, exerciseId: string) => {
    // Extract this exercise into a new single-exercise grouping immediately below the current grouping
    // using the full (unfiltered) group ordering to keep indexes stable even when difficulties are filtered.
    const fullIdx = groupingIds.indexOf(groupingId);
    const insertAt = (fullIdx === -1 ? groupings.findIndex((g) => g.id === groupingId) : fullIdx) + 1;
    onExtractExerciseToIndex(exerciseId, Math.max(0, insertAt));
  };

  const moveGroupUp = (groupingId: string) => {
    const groupIdx = groupings.findIndex((g) => g.id === groupingId);
    if (groupIdx <= 0) return;
    const ids = groupings.map((g) => g.id);
    const tmp = ids[groupIdx - 1];
    ids[groupIdx - 1] = ids[groupIdx];
    ids[groupIdx] = tmp;
    onReorderGroupings(ids);
  };

  const moveGroupDown = (groupingId: string) => {
    const groupIdx = groupings.findIndex((g) => g.id === groupingId);
    if (groupIdx === -1 || groupIdx >= groupings.length - 1) return;
    const ids = groupings.map((g) => g.id);
    const tmp = ids[groupIdx + 1];
    ids[groupIdx + 1] = ids[groupIdx];
    ids[groupIdx] = tmp;
    onReorderGroupings(ids);
  };

  return (
    <ScrollArea className="h-full">
      <div className="p-4 pt-6 space-y-2">
        {/* Top gap */}
        <div
          className="h-1.5"
          onDragOver={onCanvasDragOverGap(0)}
          onDrop={onCanvasDropAt(0)}
        >
          {canvasInsertIndex === 0 ? <div className="h-[2px] bg-gradient-to-r from-emerald-400 to-lime-400 w-full" /> : null}
        </div>

        {groupings.length === 0 && (
          <div className="text-sm text-gray-600 p-6 text-center border rounded-md bg-gray-50">
            Use “Add exercises” to start building your workout.
          </div>
        )}

        {visibleGroupings.map(({ grouping: g, visibleExercises }, visibleGroupIdx) => {
          const multi = visibleExercises.length > 1;
          return (
          <div key={g.id} className="space-y-3">
            {/* Group container */}
            <div
              className={`${multi ? "border border-slate-200/70 rounded-2xl p-4 bg-white shadow-sm" : ""}`}
              draggable
              onDragStart={handleGroupDragStart(g.id)}
              onDragOver={(e) => {
                // allow dropping an exercise onto a group container to append
                if (drag?.type === "exercise") {
                  e.preventDefault();
                }
              }}
              onDrop={(e) => {
                e.preventDefault();
                // Only treat this as a drop-on-container (append) if the drop wasn't handled by a child target.
                if (e.target !== e.currentTarget) {
                  clearDnD();
                  return;
                }
                if (drag?.type === "exercise") {
                  onMoveExerciseToGroupingEnd(g.id, drag.exerciseId);
                }
                clearDnD();
              }}
            >
              {multi ? (
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs uppercase tracking-wide text-gray-600">Circuit / Superset</div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 text-sm">
                      <div className="text-xs text-gray-600">Sets</div>
                      <Input
                        type="number"
                        value={g.sets}
                        onChange={(e) => {
                          const v = parseInt(e.target.value || "0", 10);
                          if (Number.isNaN(v) || v < 0) return;
                          onUpdateGroupingSets(g.id, v);
                        }}
                        className="h-7 w-16"
                      />
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <div className="text-xs text-gray-600">Rest</div>
                      <Input
                        type="number"
                        value={g.rest_seconds}
                        onChange={(e) => {
                          const v = parseInt(e.target.value || "0", 10);
                          if (Number.isNaN(v) || v < 0) return;
                          onUpdateGroupingRest(g.id, v);
                        }}
                        className="h-7 w-20"
                      />
                    </div>
                    <div className="flex items-center gap-1">
                      <Button size="icon" variant="ghost" onClick={() => moveGroupUp(g.id)} aria-label="Move group up" className="h-8 w-8">
                        <ArrowUp className="w-4 h-4" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => moveGroupDown(g.id)} aria-label="Move group down" className="h-8 w-8">
                        <ArrowDown className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ) : null}

              {/* Exercises */}
              {/* internal insertion gaps + exercise cards (gaps create spacing AND reorder targets) */}
              {visibleExercises.map((ex, exIdx) => (
                <div key={ex.id}>
                  {/* gap BEFORE this exercise */}
                  <div
                    className={exIdx === 0 ? "h-0" : (multi ? "h-4" : "h-0.5")}
                    onDragOver={(e) => {
                      if (drag?.type === "exercise") e.preventDefault();
                    }}
                    onDrop={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (drag?.type !== "exercise") return;

                      const order = buildFullExerciseOrderFromVisible({
                        groupingId: g.id,
                        draggedExerciseId: drag.exerciseId,
                        visibleExerciseIds: visibleExercises.map((x) => x.id),
                        dropVisibleIndex: exIdx,
                      });
                      if (!order) return clearDnD();

                      if (drag.sourceGroupingId !== g.id) {
                        await onMoveExerciseToGroupingEnd(g.id, drag.exerciseId);
                      }
                      await onReorderExercises(g.id, order);
                      clearDnD();
                    }}
                  />

                  <div
                    className={`rounded-2xl p-4 bg-white border border-slate-200/70 shadow-sm flex items-center justify-between transition-all ${combineTarget === ex.id ? "ring-2 ring-emerald-400" : "hover:border-slate-300 hover:shadow-md"}`}
                    onDragEnter={onExerciseDragEnter(ex.id)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={onExerciseDropOn(ex.id)}
                  >
                    <div className="w-full flex flex-col gap-3">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                        <div className="flex items-start gap-3 min-w-0">
                          <div
                            className="flex items-center justify-center h-9 w-9 rounded-xl bg-slate-100 text-slate-600 cursor-grab active:cursor-grabbing shrink-0"
                            draggable
                            onDragStart={handleExerciseDragStart(ex.id, g.id)}
                            onDragEnd={clearDnD}
                            aria-label="Drag to reorder"
                            role="button"
                            tabIndex={0}
                          >
                            <GripVertical className="w-4 h-4" />
                          </div>
                          <div className="h-11 w-11 rounded-xl border border-slate-200 bg-slate-50 flex items-center justify-center text-slate-500 shrink-0">
                            <Dumbbell className="w-5 h-5" />
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm font-semibold text-gray-900 truncate">
                              {exerciseNameById[ex.exercise] ?? ex.exercise}
                            </div>
                            <div className="text-xs text-slate-500 mt-0.5">Add notes</div>
                          </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-1 justify-start sm:justify-end">
                          <Button size="icon" variant="ghost" onClick={() => moveExerciseUp(g.id, ex.id)} aria-label="Move up" className="h-8 w-8">
                            <ArrowUp className="w-4 h-4" />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => moveExerciseDown(g.id, ex.id)} aria-label="Move down" className="h-8 w-8">
                            <ArrowDown className="w-4 h-4" />
                          </Button>
                          {g.exercises.length <= 1 ? (
                            <Button size="sm" variant="outline" onClick={() => joinWithAbove(g.id, ex.id)} className="gap-1 rounded-full h-8 px-2 sm:px-3" aria-label="Join with above">
                              <Link2 className="w-4 h-4" />
                              <span className="hidden sm:inline">Join ↑</span>
                            </Button>
                          ) : (
                            <Button size="sm" variant="outline" onClick={() => ungroupToBelow(g.id, ex.id)} className="gap-1 rounded-full h-8 px-2 sm:px-3" aria-label="Ungroup to below">
                              <Link2 className="w-4 h-4 rotate-180" />
                              <span className="hidden sm:inline">Ungroup ↓</span>
                            </Button>
                          )}
                          <Button size="icon" variant="ghost" onClick={() => onDeleteExercise(ex.id)} aria-label="Delete" className="h-8 w-8">
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
                        <div className="flex flex-col gap-1">
                          <div className="text-[11px] font-medium text-slate-600 leading-none">Sets</div>
                          <Input
                            inputMode="numeric"
                            className="h-9 w-full bg-white"
                            value={getDraftValue(ex.id, "sets", ex.sets)}
                            onChange={(e) => setDraftValue(ex.id, "sets", e.target.value)}
                            onBlur={() => commitDraft(ex.id, "sets", ex.sets)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") (e.currentTarget as HTMLInputElement).blur();
                              if (e.key === "Escape") {
                                setDraftNumberByKey((prev) => {
                                  const key = getDraftKey(ex.id, "sets");
                                  const { [key]: _, ...rest } = prev;
                                  return rest;
                                });
                                (e.currentTarget as HTMLInputElement).blur();
                              }
                            }}
                          />
                        </div>

                        <div className="flex flex-col gap-1">
                          <div className="text-[11px] font-medium text-slate-600 leading-none">Reps</div>
                          <Input
                            inputMode="numeric"
                            className="h-9 w-full bg-white"
                            value={getDraftValue(ex.id, "reps", ex.reps)}
                            onChange={(e) => setDraftValue(ex.id, "reps", e.target.value)}
                            onBlur={() => commitDraft(ex.id, "reps", ex.reps)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") (e.currentTarget as HTMLInputElement).blur();
                              if (e.key === "Escape") {
                                setDraftNumberByKey((prev) => {
                                  const key = getDraftKey(ex.id, "reps");
                                  const { [key]: _, ...rest } = prev;
                                  return rest;
                                });
                                (e.currentTarget as HTMLInputElement).blur();
                              }
                            }}
                          />
                        </div>

                        <div className="flex flex-col gap-1">
                          <div className="text-[11px] font-medium text-slate-600 leading-none">
                            Time <span className="text-slate-400">(s)</span>
                          </div>
                          <Input
                            inputMode="numeric"
                            className="h-9 w-full bg-white"
                            value={getDraftValue(ex.id, "time_seconds", ex.time_seconds)}
                            onChange={(e) => setDraftValue(ex.id, "time_seconds", e.target.value)}
                            onBlur={() => commitDraft(ex.id, "time_seconds", ex.time_seconds)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") (e.currentTarget as HTMLInputElement).blur();
                              if (e.key === "Escape") {
                                setDraftNumberByKey((prev) => {
                                  const key = getDraftKey(ex.id, "time_seconds");
                                  const { [key]: _, ...rest } = prev;
                                  return rest;
                                });
                                (e.currentTarget as HTMLInputElement).blur();
                              }
                            }}
                          />
                        </div>

                        <div className="flex flex-col gap-1">
                          <div className="text-[11px] font-medium text-slate-600 leading-none">
                            Rest <span className="text-slate-400">(s)</span>
                          </div>
                          <Input
                            inputMode="numeric"
                            className="h-9 w-full bg-white"
                            value={getDraftValue(ex.id, "rest_seconds", ex.rest_seconds)}
                            onChange={(e) => setDraftValue(ex.id, "rest_seconds", e.target.value)}
                            onBlur={() => commitDraft(ex.id, "rest_seconds", ex.rest_seconds)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") (e.currentTarget as HTMLInputElement).blur();
                              if (e.key === "Escape") {
                                setDraftNumberByKey((prev) => {
                                  const key = getDraftKey(ex.id, "rest_seconds");
                                  const { [key]: _, ...rest } = prev;
                                  return rest;
                                });
                                (e.currentTarget as HTMLInputElement).blur();
                              }
                            }}
                          />
                        </div>

                        <div className="flex flex-col gap-1">
                          <div className="text-[11px] font-medium text-slate-600 leading-none">kg</div>
                          <Input
                            inputMode="numeric"
                            className="h-9 w-full bg-white"
                            value={getDraftValue(ex.id, "weight_kg", ex.weight_kg)}
                            onChange={(e) => setDraftValue(ex.id, "weight_kg", e.target.value)}
                            onBlur={() => commitDraft(ex.id, "weight_kg", ex.weight_kg)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") (e.currentTarget as HTMLInputElement).blur();
                              if (e.key === "Escape") {
                                setDraftNumberByKey((prev) => {
                                  const key = getDraftKey(ex.id, "weight_kg");
                                  const { [key]: _, ...rest } = prev;
                                  return rest;
                                });
                                (e.currentTarget as HTMLInputElement).blur();
                              }
                            }}
                          />
                        </div>

                        <div className="flex flex-col gap-1">
                          <div className="text-[11px] font-medium text-slate-600 leading-none">lbs</div>
                          <Input
                            inputMode="numeric"
                            className="h-9 w-full bg-white"
                            value={getDraftValue(ex.id, "weight_lbs", ex.weight_lbs)}
                            onChange={(e) => setDraftValue(ex.id, "weight_lbs", e.target.value)}
                            onBlur={() => commitDraft(ex.id, "weight_lbs", ex.weight_lbs)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") (e.currentTarget as HTMLInputElement).blur();
                              if (e.key === "Escape") {
                                setDraftNumberByKey((prev) => {
                                  const key = getDraftKey(ex.id, "weight_lbs");
                                  const { [key]: _, ...rest } = prev;
                                  return rest;
                                });
                                (e.currentTarget as HTMLInputElement).blur();
                              }
                            }}
                          />
                        </div>

                        <div className="flex flex-col gap-1">
                          <div className="text-[11px] font-medium text-slate-600 leading-none">Difficulty</div>
                          <Select value={ex.difficulty} onValueChange={(v) => onUpdateExercise(ex.id, { difficulty: v as any })}>
                            <SelectTrigger className="h-9 w-full bg-white">
                              <SelectValue placeholder="difficulty" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Beginner">Beginner</SelectItem>
                              <SelectItem value="Intermediate">Intermediate</SelectItem>
                              <SelectItem value="Advanced">Advanced</SelectItem>
                              <SelectItem value="Expert">Expert</SelectItem>
                              <SelectItem value="Pro">Pro</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* gap AFTER last exercise */}
              <div
                className={multi ? "h-4" : "h-0.5"}
                onDragOver={(e) => {
                  if (drag?.type === "exercise") e.preventDefault();
                }}
                onDrop={async (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (drag?.type !== "exercise") return;

                  const order = buildFullExerciseOrderFromVisible({
                    groupingId: g.id,
                    draggedExerciseId: drag.exerciseId,
                    visibleExerciseIds: visibleExercises.map((x) => x.id),
                    dropVisibleIndex: visibleExercises.length,
                  });
                  if (!order) return clearDnD();

                  if (drag.sourceGroupingId !== g.id) {
                    await onMoveExerciseToGroupingEnd(g.id, drag.exerciseId);
                  }
                  await onReorderExercises(g.id, order);
                  clearDnD();
                }}
              />
            </div>

            {/* Canvas gap after this group */}
            <div className={multi ? "h-4" : "h-0.5"} onDragOver={onCanvasDragOverGap(visibleGroupIdx + 1)} onDrop={onCanvasDropAt(visibleGroupIdx + 1)}>
              {canvasInsertIndex === visibleGroupIdx + 1 ? <div className="h-[2px] bg-gradient-to-r from-emerald-400 to-lime-400 w-full" /> : null}
            </div>
          </div>
          );
        })}
      </div>
    </ScrollArea>
  );
}


